import { ChangeDetectionStrategy, Component, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { Dialog } from 'primeng/dialog';
import { TranslocoPipe } from '@jsverse/transloco';

import { SkinningService } from '@core/skin';
import { PlayerService } from '@features/player/data-access/player.service';
import { IExtrasItemDto } from '@features/assets/data-access/api-shared.dto';
import { RoundedIconButton } from 'app/shared/components/rounded-icon-button/rounded-icon-button';
import { TabsNav } from 'app/shared/components/tabs-nav/tabs-nav';

import { DetailsStore } from '@features/details/data-access/details.store';
import { DetailsService } from '@features/details/data-access/details.service';
import { parseDetailsHash } from '@features/details/data-access/details-hash';
import { DetailsContentType } from '@features/details/data-access/details.models';
import { DetailsHeader } from '@features/details/components/details-header/details-header';
import { DetailsActions } from '@features/details/components/details-actions/details-actions';
import { DetailsDescription } from '@features/details/components/details-description/details-description';
import { DetailsSeries } from '@features/details/components/details-series/details-series';
import { DetailsVod } from '@features/details/components/details-vod/details-vod';
import { DetailsLive } from '@features/details/components/details-live/details-live';
import { DetailsExtras } from '@features/details/components/details-extras/details-extras';
import { DetailsSkeleton } from '@features/details/components/details-skeleton/details-skeleton';

/**
 * The one smart component of the details feature. Mounted once in the app shell, it listens for the
 * `#details?<base64>` hash (the legacy base64 scheme `DetailsService.open()` writes), drives
 * the store, hosts the `p-dialog`, and dispatches the lower section by active tab. All actions go
 * through `DetailsService`; all state is read from `DetailsStore`.
 */
@Component({
    selector: 'details-shell',
    standalone: true,
    imports: [
        Dialog, RoundedIconButton, TabsNav, TranslocoPipe,
        DetailsHeader, DetailsActions, DetailsDescription,
        DetailsSeries, DetailsVod, DetailsLive, DetailsExtras, DetailsSkeleton,
    ],
    templateUrl: './details-shell.html',
    host: { class: 'contents' },
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsShell {
    protected readonly store = inject(DetailsStore);
    protected readonly svc = inject(DetailsService);
    protected readonly skinning = inject(SkinningService);
    private readonly location = inject(Location);
    private readonly router = inject(Router);
    private readonly player = inject(PlayerService);

    /**
     * Skin setting `item_details_position` picks the layout (matches the legacy hsx-item-details):
     * `'popup'` (default) = centered modal, `'slide'` = right-pinned full-height sidebar.
     */
    protected readonly sidebar = this.skinning.getSetting('item_details_position') === 'slide';

    /** Last handled `#`-fragment — dedupes the three triggers below against each other. */
    private previousFragment: string | null = null;

    constructor() {
        // Open whenever the URL carries `#details`, from any entry path:
        //  • now — a hash already present on first load (direct link / refresh);
        //  • NavigationEnd — navigations that settle after this component mounts, e.g. the
        //    login → returnUrl bounce-back, whose initial event fired before we subscribed;
        //  • onUrlChange — hash-only opens via open() → location.replaceState (clicks).
        this.handleUrl();
        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            takeUntilDestroyed(),
        ).subscribe(() => this.handleUrl());
        this.location.onUrlChange(() => this.handleUrl());

        // Back on the details after playback: the player drops its item only once the closing
        // session report (and with it the bookmark) is through, so watch-data read now is current.
        let hadItem = false;
        effect(() => {
            const hasItem = !!this.player.getItem()();
            if (hadItem && !hasItem && untracked(() => this.store.open())) {
                untracked(() => this.store.refreshWatchData());
            }
            hadItem = hasItem;
        });
    }

    private handleUrl(): void {
        const hash = window.location.hash; // source of truth, fragment only
        const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
        if (fragment === this.previousFragment) return;
        this.previousFragment = fragment;

        // The current modal content (which hosts the relocated player) is about to be replaced
        // (item switch) or closed; return the player to its persistent host first so its embedded
        // view isn't destroyed together with the modal.
        this.svc.releasePreview();

        const params = parseDetailsHash(`#${fragment}`);
        if (params) this.store.loadByHash(params);
        else if (this.store.open()) this.store.close();
    }

    /**
     * Play pressed in the modal (main play button or an extra). Launch playback and KEEP the
     * modal open: the normal modal already stacks below the fullscreen player
     * (`.details-mask-below-player`), so playback covers it — and when the player closes, the
     * customer is back on the details, able to play again or pick something else without
     * searching from scratch. Nothing happens on a no-op play (nothing playable).
     */
    protected play(): void {
        if (this.svc.play()) this.afterPlayLaunched();
    }

    protected playExtra(extra: IExtrasItemDto): void {
        if (this.svc.playExtra(extra)) this.afterPlayLaunched();
    }

    /**
     * Two variants still dismiss:
     * - overPlayer (details opened from the player chrome) stacks ABOVE the player and would
     *   cover the playback it just started;
     * - Live: the player chrome re-opens the airing event's details on demand (see
     *   live-details-reopen.spec), and that path expects to open them ABOVE the player —
     *   PrimeNG cannot restack an already-open dialog from below to above, so the live modal
     *   must not linger underneath.
     */
    private afterPlayLaunched(): void {
        if (this.store.overPlayer() || this.store.item()?.contentType === DetailsContentType.Live) {
            this.dismiss();
        }
    }

    /**
     * Close the modal after playback was launched. Unlike close(), does NOT releasePreview():
     * play()/playExtra() already relocated the player to its default host and started Normal
     * playback, so returning the preview here would tear that just-started player down.
     */
    private dismiss(): void {
        this.svc.close();
        this.previousFragment = null;
    }

    /** p-dialog closed via mask click — mirror it to a full close (state + hash strip). */
    protected onVisibleChange(visible: boolean): void {
        if (!visible && this.store.open()) this.close();
    }

    /**
     * The dialog finished its hide animation: now the content can go. Guarded, because a new
     * item may have been opened while the previous dialog was still fading out — resetting then
     * would wipe the load that is in flight.
     */
    protected onHidden(): void {
        if (!this.store.open()) this.store.reset();
    }

    /** Close from chrome (button / mask). Reset the fragment so reopening the same item reloads. */
    protected close(): void {
        // Move the player home before the modal content is destroyed (svc.close() strips the hash via
        // raw history, so handleUrl won't fire for this path — release here explicitly).
        this.svc.releasePreview();
        this.svc.close();
        this.previousFragment = null;
    }
}
